import { useEffect, useMemo, useState } from 'react';
import { PlusBlue, XGray } from '../../icons/SvgIcons';
import { PrimaryButton, TextButton, WhiteButton } from '../ui/Button/Buttons';
import { useAppSelector } from '../../hooks/redux';
import { convertToDropdownOptions } from '../../utils/AiSystems.util';
import {
  AssignAiSystemToUserRequest,
  assignAiSystemToUserWithoutInvitation
} from '../../services/TeamService';
import { EMAIL_REGEX } from '../../constants/Validation.constants';
import { useDispatch } from 'react-redux';
import {
  inviteUserFailure,
  inviteUserStart,
  inviteUserSuccess,
  setIsUserInvited
} from '../../store/slices/userManagementSlice';
import { useUserManagement } from '../hooks/useUserManagement';
import { AISystem, AiSystemUserQuotaCheck } from '../../types/AISystems.types';
import { checkAiSystemUserQuota } from '../../services/AISystemsService';
import Loader from '../ui/Loader/Loader';
import DropdownPortal from '../ui/DropdownPortal/DropdownPortal';
import { Role } from '../../types/Team.types';

const isEmailValid = (value: string) => EMAIL_REGEX.test(value);

type SystemRolePair = {
  id: string; // unique identifier for each pair
  aiSystemId: string;
  role: string;
};

type AssignUserProps = Readonly<{
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  registeredUserEmail?: string;
  assignListForAiSystemFromUser?: AISystem[];
  userRoles?: Role[];
}>;

export function AssignUser({
  setIsOpen,
  registeredUserEmail,
  assignListForAiSystemFromUser,
  userRoles = []
}: AssignUserProps) {
  const [email, setEmail] = useState(registeredUserEmail || '');
  const [systemRolePairs, setSystemRolePairs] = useState<SystemRolePair[]>([
    { id: crypto.randomUUID(), aiSystemId: '', role: '' }
  ]);
  const [fetchingQuota, setFetchingQuota] = useState(false);
  const [quotaBySystemId, setQuotaBySystemId] = useState<
    Record<string, AiSystemUserQuotaCheck>
  >({});
  const aiSystems = useAppSelector((state) => state.aiSystems.systems);
  const account = useAppSelector((state) => state.accountDefault.account);
  const teamUsers = useAppSelector((state) => state.userManagement.teamUsers);
  const dispatch = useDispatch();

  const aiSystemOptions = useMemo(() => {
    return convertToDropdownOptions(aiSystems || [], 'name', 'id');
  }, [aiSystems]);

  const availableAiSystemOptions = useMemo(() => {
    if (
      !assignListForAiSystemFromUser ||
      assignListForAiSystemFromUser.length === 0
    ) {
      return aiSystemOptions;
    }

    const assignedSystemIds = assignListForAiSystemFromUser.map(
      (system) => system.id
    );

    // Filter already assigned systems to the user
    return aiSystemOptions.filter(
      (option) => !assignedSystemIds.includes(option.value)
    );
  }, [aiSystemOptions, assignListForAiSystemFromUser]);

  const userEmailOptions = useMemo(() => {
    return (teamUsers ?? []).map((user) => ({
      value: user.email,
      label: user.email
    }));
  }, [teamUsers]);

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    if (!EMAIL_REGEX.test(email)) return 'Invalid email format.';
    return '';
  };

  const [emailError, setEmailError] = useState<string>('');
  const { assignAiSystemWithoutInvitation } = useUserManagement();

  useEffect(() => {
    if (!isEmailValid(email)) {
      return;
    }

    const selectedSystemIds = systemRolePairs
      .map((pair) => pair.aiSystemId)
      .filter(Boolean);

    if (selectedSystemIds.length === 0) {
      return;
    }

    let cancelled = false;
    const fetchQuotaChecks = async () => {
      setFetchingQuota(true);
      try {
        const results = await Promise.all(
          selectedSystemIds.map((aiSystemId) =>
            checkAiSystemUserQuota(aiSystemId, email)
          )
        );
        if (cancelled) return;
        setQuotaBySystemId((prev) => {
          const next = { ...prev };
          selectedSystemIds.forEach((aiSystemId, index) => {
            next[aiSystemId] = results[index];
          });
          return next;
        });
      } catch (error) {
        console.error('Failed to fetch quota check:', error);
      } finally {
        if (!cancelled) {
          setFetchingQuota(false);
        }
      }
    };

    fetchQuotaChecks();
    return () => {
      cancelled = true;
    };
  }, [email, systemRolePairs]);

  const quotaErrorsByPair = useMemo(() => {
    if (!isEmailValid(email)) {
      return {};
    }

    const errors: Record<string, string> = {};
    for (const pair of systemRolePairs) {
      if (!pair.aiSystemId) continue;
      const quota = quotaBySystemId[pair.aiSystemId];
      if (quota && !quota.canAdd) {
        errors[pair.id] = 'Quota exceeded for this account';
      }
    }
    return errors;
  }, [email, systemRolePairs, quotaBySystemId]);

  const isFormValid = useMemo(() => {
    if (email === '') return false;
    if (emailError) return false;

    for (const pair of systemRolePairs) {
      if (!pair.aiSystemId || !pair.role) {
        return false;
      }
    }

    if (Object.keys(quotaErrorsByPair).length > 0) {
      return false;
    }

    return true;
  }, [systemRolePairs, emailError, email, quotaErrorsByPair]);

  const handleAddAnotherAISystem = () => {
    setSystemRolePairs([
      ...systemRolePairs,
      { id: crypto.randomUUID(), aiSystemId: '', role: '' }
    ]);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleRemoveSystem = (id: string) => {
    if (systemRolePairs.length === 1) return;
    setSystemRolePairs(systemRolePairs.filter((pair) => pair.id !== id));
  };

  const updateSystemValue = async (id: string, aiSystemId: string) => {
    const currentPair = systemRolePairs.find((pair) => pair.id === id);

    // ignore selection if AI system is unchanged
    if (currentPair?.aiSystemId === aiSystemId) {
      return;
    }

    setSystemRolePairs(
      systemRolePairs.map((pair) =>
        pair.id === id ? { ...pair, aiSystemId } : pair
      )
    );
  };

  const updateRoleValue = (id: string, role: string) => {
    setSystemRolePairs(
      systemRolePairs.map((pair) => (pair.id === id ? { ...pair, role } : pair))
    );
  };

  const createRequestBody = (): AssignAiSystemToUserRequest => {
    return {
      email,
      aiSystemGrants: systemRolePairs.map(({ aiSystemId, role }) => ({
        aiSystemId,
        role
      }))
    };
  };

  const submitAssignUser = async () => {
    const inviteData = createRequestBody();
    if (registeredUserEmail === undefined) {
      dispatch(inviteUserStart());
      try {
        setIsOpen(false);
        await assignAiSystemToUserWithoutInvitation(account!.id, inviteData);
        dispatch(inviteUserSuccess());
        dispatch(setIsUserInvited(true));
      } catch (error) {
        console.error('Failed to send invite:', error);
        dispatch(inviteUserFailure());
        return;
      }
    } else {
      setIsOpen(false);
      await assignAiSystemWithoutInvitation(account!.id, inviteData);
    }
  };

  const handleAssignUser = async () => {
    if (!isFormValid) return;

    await submitAssignUser();
  };

  const isAllSystemsAssigned = useMemo(() => {
    if (systemRolePairs.length >= availableAiSystemOptions.length) {
      return true;
    }
    const completelyAssignedPairs = systemRolePairs.filter(
      (pair) => pair.aiSystemId !== '' && pair.role !== ''
    );
    return completelyAssignedPairs.length >= availableAiSystemOptions.length;
  }, [systemRolePairs, availableAiSystemOptions]);

  return (
    <div className="flex flex-col flex-start gap-6">
      <h2 className="text-lg leading-6 font-medium text-gray-900">
        {registeredUserEmail ? 'Assign AI System' : 'Assign User'}
      </h2>
      {!registeredUserEmail && (
        <div className="flex flex-col w-full max-w-[740px]">
          <DropdownPortal
            label="Select user"
            placeholder="Select user"
            options={userEmailOptions}
            allOptions={userEmailOptions}
            value={email}
            onChange={(value) => {
              setEmail(value);
              setEmailError(validateEmail(value));
            }}
            error={emailError}
            searchable
            searchInTrigger
            searchPlaceholder="Search user"
            noOptionsMessage="No users found"
          />
        </div>
      )}

      <div className="flex flex-col max-h-[585px]">
        <div className="flex flex-col gap-6 overflow-y-auto invite-scroll scrollbar-hide">
          {systemRolePairs.map((pair) => (
            <div
              key={pair.id}
              className="flex flex-row items-end gap-6 w-full relative"
            >
              <div className="flex flex-row gap-6 flex-1 max-w-[740px] pl-px">
                <DropdownPortal
                  label="AI System"
                  placeholder="Select AI system"
                  allOptions={aiSystemOptions}
                  error={quotaErrorsByPair[pair.id]}
                  options={availableAiSystemOptions.filter(
                    (option) =>
                      !systemRolePairs
                        .filter((p) => p.id !== pair.id)
                        .some((p) => p.aiSystemId === option.value)
                  )}
                  value={pair.aiSystemId}
                  onChange={(value) => updateSystemValue(pair.id, value)}
                  className="max-w-[calc((50%-12px))] w-full"
                  searchable
                  searchInTrigger
                  searchPlaceholder="Search AI system"
                  noOptionsMessage="No AI systems found"
                />
                <DropdownPortal
                  label="Role"
                  placeholder="Choose role"
                  options={userRoles.map((role) => ({
                    value: role,
                    label: role.charAt(0) + role.slice(1).toLowerCase()
                  }))}
                  allOptions={userRoles.map((role) => ({
                    value: role,
                    label: role.charAt(0) + role.slice(1).toLowerCase()
                  }))}
                  value={pair.role}
                  disabled={pair.aiSystemId === ''}
                  onChange={(value) => updateRoleValue(pair.id, value)}
                />
              </div>
              <button
                type="button"
                className="absolute right-0 bottom-2 cursor-pointer"
                onClick={() => handleRemoveSystem(pair.id)}
              >
                {systemRolePairs.length > 1 ? XGray(false) : XGray(true)}
              </button>
            </div>
          ))}
          <div>
            <TextButton
              onClick={handleAddAnotherAISystem}
              icon={PlusBlue(isAllSystemsAssigned)}
              disabled={isAllSystemsAssigned}
            >
              Add Another AI System
            </TextButton>
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-end items-center gap-3">
        {fetchingQuota && (
          <div className="mr-auto">
            <Loader width="20" height="20" />
          </div>
        )}
        <WhiteButton onClick={() => setIsOpen(false)}>Cancel</WhiteButton>
        <PrimaryButton disabled={!isFormValid} onClick={handleAssignUser}>
          {registeredUserEmail ? 'Assign AI System' : 'Invite User'}
        </PrimaryButton>
      </div>
    </div>
  );
}
